import { ELEMENT_MENTION_TICKER } from '@components/RichTextPlate/plugins/createTickerMentionPlugin'
import { insertNodes, insertText, moveSelection, SlateEditor } from '@udecode/plate-common'
import { BaseMentionPlugin, MentionOnSelectItem } from '@udecode/plate-mention'
import { Channel, Mention } from 'use-towns-client'
import { getPrettyDisplayName, UserWithDisplayName } from 'utils/getPrettyDisplayName'

import { ELEMENT_MENTION_CHANNEL } from '../../../plugins/createChannelPlugin'
import { ELEMENT_SLASH_COMMAND } from '../../../plugins/createSlashCommandPlugin'
import { getMentionOnSelectItem } from '../../../plugins/getMentionOnSelectItem'

import {
    AtChannelUser,
    ComboboxTypes,
    TComboboxItemWithData,
    TSlashCommand,
    TSlashCommandElement,
    TUserIDNameMap,
    TUserMention,
    TUserMentionElement,
    TUserWithChannel,
} from './types'

export const MAX_AUTOCOMPLETE_SUGGESTIONS = 15

/**
 * @desc Recursively go through the nodes to extract all the `Mention` nodes
 */
export const getMentions = (children: TUserMentionElement[]): Mention[] => {
    const mentions: Mention[] = []
    children.map((node) => recursivelyGetNameAndId(node, mentions))
    return mentions
}

const recursivelyGetNameAndId = (node: TUserMentionElement, mentions: Mention[]) => {
    if (Array.isArray(node.children) && node.children.length > 0) {
        node.children.map((n) => recursivelyGetNameAndId(n as TUserMentionElement, mentions))
    }

    if (node.type === BaseMentionPlugin.key) {
        mentions.push({
            displayName: (node as TUserMentionElement).value,
            userId: (node as TUserMentionElement).userId,
            atChannel: (node as TUserMentionElement).atChannel,
        })
    }
}

/**
 * @desc Recursively search for the first slash command in the message
 * Returns the first slash command found (only one allowed per message)
 */
export const getSlashCommand = (
    children: TUserMentionElement[],
): TSlashCommandElement | undefined => {
    for (const node of children) {
        // Check if this node is a slash command
        if ('type' in node && node.type === ELEMENT_SLASH_COMMAND) {
            return node as unknown as TSlashCommandElement
        }

        // Recursively check children if they exist
        if ('children' in node && Array.isArray(node.children) && node.children.length > 0) {
            const found = getSlashCommand(node.children as unknown as TUserMentionElement[])
            if (found) {
                return found
            }
        }
    }

    return undefined
}

export const userMentionFilter =
    (query: string) =>
    (item: TComboboxItemWithData<TUserWithChannel>): boolean =>
        [item.data.username, item.data.displayName, item.data.ensName]
            .join(' ')
            .toLowerCase()
            .includes(query.toLowerCase())

export const channelMentionFilter =
    (query: string) =>
    (item: TComboboxItemWithData<Channel>): boolean =>
        item.data.label.toLowerCase().includes(query.toLowerCase())

export const getUsernameForMention = <T extends TUserWithChannel>(
    comboboxType: ComboboxTypes,
    item: TComboboxItemWithData<T>,
): string | undefined => {
    if (comboboxType !== ComboboxTypes.userMention || item.data.atChannel) {
        return undefined
    }

    return item.data.username ? `@${item.data.username}` : undefined
}

export const getUserHashMap = (
    users: (Omit<UserWithDisplayName, 'userId'> & { userId?: string })[],
): TUserIDNameMap => {
    if (!Array.isArray(users)) {
        return {}
    }
    const map: TUserIDNameMap = {}
    users.forEach((user) => {
        if (user.userId && user.userId !== AtChannelUser.userId) {
            map[user.userId] = getPrettyDisplayName(user as UserWithDisplayName, true)
        }
    })
    return map
}

export const convertUserToCombobox = (
    user: TUserMention,
    channelMemberIds: string[],
): TComboboxItemWithData<TUserWithChannel> => ({
    text: getPrettyDisplayName(user),
    key: user.userId,
    data: { ...user, isChannelMember: channelMemberIds.includes(user.userId) },
})

const onSelectItemUser: MentionOnSelectItem<TComboboxItemWithData> = getMentionOnSelectItem({
    key: BaseMentionPlugin.key,
})

const onSelectItemChannel: MentionOnSelectItem<TComboboxItemWithData> = getMentionOnSelectItem({
    key: ELEMENT_MENTION_CHANNEL,
})

const onSelectItemTicker: MentionOnSelectItem<TComboboxItemWithData> = getMentionOnSelectItem({
    key: ELEMENT_MENTION_TICKER,
})

/**
 * On select emoji, insert the emoji as text into the editor, instead of creating a complex element as required for @user #channel mention.
 */
const onSelectItemEmoji: MentionOnSelectItem<TComboboxItemWithData> = (
    editor: SlateEditor,
    item,
    _search?,
) => {
    editor.insertText(`${item.key} `)
}

/**
 * On select slash command, insert the slash command element into the editor.
 */
const onSelectItemSlashCommand: MentionOnSelectItem<TComboboxItemWithData<TSlashCommand>> = (
    editor: SlateEditor,
    item,
    _search?,
) => {
    const slashCommand = item.data
    // Insert the slash command as an inline void element
    const slashCommandNode = {
        type: ELEMENT_SLASH_COMMAND,
        command: slashCommand,
        appAddress: slashCommand.appAddress,
        appClientAddress: slashCommand.appClientAddress,
        value: `/${slashCommand.name}`,
        children: [{ text: `/${slashCommand.name}` }],
    } satisfies TSlashCommandElement
    insertNodes(editor, slashCommandNode)
    // Move cursor after the element and add a space
    moveSelection(editor, { unit: 'offset' })
    insertText(editor, ' ')
}

export const onMentionSelectTriggerMap = (trigger: string) => {
    const handlers = {
        '@': onSelectItemUser,
        '#': onSelectItemChannel,
        ':': onSelectItemEmoji,
        $: onSelectItemTicker,
        '/': onSelectItemSlashCommand,
    } as Record<string, MentionOnSelectItem<TComboboxItemWithData>>
    return handlers[trigger as keyof typeof handlers]
}

export const TRIGGER_BEHAVIORS = {
    '@': {
        type: ComboboxTypes.userMention,
        fixedPosition: false,
        fullWidth: false,
    },
    '#': {
        type: ComboboxTypes.channelMention,
        fixedPosition: false,
        fullWidth: false,
    },
    ':': {
        type: ComboboxTypes.emojiMention,
        fixedPosition: false,
        fullWidth: false,
    },
    $: {
        type: ComboboxTypes.tickerMention,
        fixedPosition: false,
        fullWidth: false,
    },
    '/': {
        type: ComboboxTypes.slashCommand,
        fixedPosition: true,
        fullWidth: true,
    },
}

export type TriggerBehavior = (typeof TRIGGER_BEHAVIORS)[keyof typeof TRIGGER_BEHAVIORS]

export const getTriggerBehavior = (trigger: string): TriggerBehavior | undefined => {
    return TRIGGER_BEHAVIORS[trigger as keyof typeof TRIGGER_BEHAVIORS]
}

export const getTriggerType = (trigger: string): ComboboxTypes | undefined => {
    const behavior = getTriggerBehavior(trigger)
    return behavior?.type
}

export const isUserCombobox = (trigger: string) =>
    getTriggerType(trigger) === ComboboxTypes.userMention
export const isChannelCombobox = (trigger: string) =>
    getTriggerType(trigger) === ComboboxTypes.channelMention
export const isEmojiCombobox = (trigger: string) =>
    getTriggerType(trigger) === ComboboxTypes.emojiMention
export const isTickerCombobox = (trigger: string) =>
    getTriggerType(trigger) === ComboboxTypes.tickerMention
export const isSlashCommandCombobox = (trigger: string) =>
    getTriggerType(trigger) === ComboboxTypes.slashCommand
